import React from "react";
import { useHistory } from "react-router-dom";
import {
  DocumentArrowUpIcon,
  BuildingLibraryIcon,
  ArrowRightIcon,
  ChatBubbleLeftIcon,
  PlusCircleIcon,
  ScaleIcon,
} from "@heroicons/react/24/outline";
import { useProfile } from "@moonscape/shared";
import AddTransaction from "@/components/AddTransaction/AddTransaction";
import { QRCodePromotion } from "@/components/QRCodePromotion";
import { DHead, Lab } from "@/components/ms";

/** One start route: hairline rules, lead icon, chevron. */
const StartRow: React.FC<{
  icon: React.ReactNode;
  title: string;
  body: string;
  className?: string;
}> = ({ icon, title, body, className }) => (
  <div
    className={`flex min-h-[44px] items-center gap-3.5 border-t border-ms-line px-2 py-[15px] text-left transition-colors hover:bg-ms-card2 ${className || ""}`}
  >
    <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-ms-mint-soft text-ms-link">
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-[14.5px] font-semibold tracking-[-0.015em]">{title}</span>
      <span className="mt-[3px] block text-[12.5px] leading-[1.45] text-ms-mute">{body}</span>
    </span>
    <ArrowRightIcon className="h-4 w-4 flex-none text-ms-dim" />
  </div>
);

export const QuickStartScreen: React.FC = () => {
  const history = useHistory();

  // Keeps the profile fresh while the user sets things up (unchanged from before).
  useProfile({ refetchOnWindowFocus: true });

  const handleNavigation = (path: string, e: React.MouseEvent) => {
    e.preventDefault();
    history.push(path);
  };

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-ms-bg text-ms-ink">
      <div className="mx-auto max-w-3xl px-[var(--pad)] py-12">
        {/* Header */}
        <div className="mb-10">
          <Lab size={9.5}>Quick start</Lab>
          <h1 className="mt-2.5 text-[27px] font-semibold leading-[1.15] tracking-[-0.032em] lg:text-[34px]">
            Welcome to Moonscape
          </h1>
          <p className="mt-2.5 max-w-[31em] text-[14px] leading-[1.5] text-ms-mute">
            Tell us where you file, connect one source, and we'll build your number from there.
          </p>
        </div>

        {/* Bring your transactions in */}
        <DHead label="Bring your transactions in" />
        <div className="mb-10 mt-1">
          <a
            href="/quiz"
            onClick={(e) => handleNavigation("/quiz", e)}
            className="block"
          >
            <StartRow
              icon={<ScaleIcon className="h-[18px] w-[18px]" />}
              title="Tell us where you file"
              body="Picks the right rules, forms and rates — takes about 15 seconds"
              className="border-t-0"
            />
          </a>

          <a
            href="/integrations"
            onClick={(e) => handleNavigation("/integrations", e)}
            className="block"
          >
            <StartRow
              icon={<BuildingLibraryIcon className="h-[18px] w-[18px]" />}
              title="Connect a source"
              body="An exchange, a wallet address or a chain — read-only, always"
            />
          </a>

          <a
            href="/integrations"
            onClick={(e) => handleNavigation("/integrations", e)}
            className="block"
          >
            <StartRow
              icon={<DocumentArrowUpIcon className="h-[18px] w-[18px]" />}
              title="Upload a CSV export"
              body="An equal path — we read exchange exports as they come"
            />
          </a>

          <AddTransaction className="block cursor-pointer">
            <StartRow
              icon={<PlusCircleIcon className="h-[18px] w-[18px]" />}
              title="Add transaction"
              body="Add individual crypto transactions manually"
            />
          </AddTransaction>
        </div>

        {/* App + help */}
        <div className="grid grid-cols-1 gap-[var(--gap)] md:grid-cols-2">
          <div className="border border-ms-line bg-ms-card p-5">
            <QRCodePromotion variant="card" />
          </div>

          <div className="border border-ms-line bg-ms-card p-5">
            <div className="flex items-start gap-3.5">
              <ChatBubbleLeftIcon className="h-6 w-6 flex-none text-ms-link" />
              <div className="min-w-0">
                <h3 className="text-[14.5px] font-semibold tracking-[-0.015em]">
                  Need help or have feedback?
                </h3>
                <p className="mt-1.5 text-[12.5px] leading-[1.5] text-ms-mute">
                  We'd love to hear from you. Reach out at{" "}
                  <a
                    href="mailto:hello@moonscape.app"
                    className="text-ms-link transition-colors hover:text-ms-green"
                  >
                    hello@moonscape.app
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickStartScreen;
